'use client';

import { useContext, useEffect } from 'react';

import type {
  CheckoutLifecycleEvent,
  CheckoutLifecyclePhase,
} from './CheckoutLifecycleEvent';
import { CheckoutNotificationContext } from './context';

/**
 * Subscribe to ONE checkout lifecycle phase (`success` / `ended` / `canceled` /
 * `error`). The shared implementation behind {@link useOnCheckoutSuccess},
 * {@link useOnCheckoutEnded}, {@link useOnCheckoutCanceled}, {@link useOnCheckoutError}.
 * No-ops gracefully without a {@link CheckoutNotificationProvider} ancestor (e.g. an
 * isolated story).
 *
 * `listener` must be referentially stable across renders (rely on the React
 * Compiler, or a memoized handler) — it's an effect dependency, so a fresh
 * function every render would re-subscribe each time.
 */
export function useOnCheckoutLifecycle(
  phase: CheckoutLifecyclePhase,
  listener: (event: CheckoutLifecycleEvent) => void,
): void {
  const context = useContext(CheckoutNotificationContext);
  useEffect(() => {
    if (context == null) return;
    return context.subscribe(event => {
      if (event.phase === phase) listener(event);
    });
  }, [context, phase, listener]);
}
